import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import type { StrictModifiers } from '@popperjs/core';
import Popper, { Placement } from '../Popper';
import { Wrap, Block, Corner, Arrow, ArrowVert } from './styled';

export interface TooltipProps {
  title?: React.ReactNode;
  arrow?: boolean;
  placement?: string | Placement;
  children: React.ReactElement;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  backgroundColor?: string;
  flip?: boolean;
  style?: React.CSSProperties;
  onShow?: () => void;
}

export { Placement as TooltipPlacement };

const mergeRefs = (...refs: any[]) => (node: any) => {
  refs.forEach(ref => {
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  });
};

const Tooltip = forwardRef<HTMLElement, TooltipProps>((props, ref) => {
  const {
    title, arrow, placement = Placement.Bottom, children,
    width, minWidth = 0, maxWidth, backgroundColor, flip, style, onShow
  } = props;

  const [open, setOpen] = useState(false);
  const [targetNode, setTargetNode] = useState<HTMLElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);

  const child = React.Children.only(children);

  useEffect(() => {
    if (open) onShow?.();
  }, [open, onShow]);

  const modifiers = useMemo<StrictModifiers[]>(() => {
    const skidding = ['top', 'bottom'].includes(placement as string) ? 0 
      : ['top-end', 'bottom-end'].includes(placement as string) ? 16 : -16;

    return [
      ...(flip ? [{
        name: 'flip',
        options: { fallbackPlacements: ['top-start', 'top', 'top-end', 'bottom-start', 'bottom', 'bottom-end'] },
      } as StrictModifiers] : []),
      { name: 'arrow', options: { padding: 16 } },
      { name: 'offset', options: { offset: [skidding, 5] } },
    ];
  }, [placement, flip]);

  const blockStyles = useMemo<React.CSSProperties>(() => ({
    minWidth,
    maxWidth: Math.max(minWidth, maxWidth || width || 0) || undefined,
    width,
    background: backgroundColor,
    ...style,
  }), [minWidth, maxWidth, width, backgroundColor, style]);

  return (
    <>
      {React.cloneElement(child, {
        ref: mergeRefs(setTargetNode, ref, (child as any).ref),
        onMouseEnter: (e: any) => { setOpen(true); child.props.onMouseEnter?.(e); },
        onMouseLeave: (e: any) => { setOpen(false); child.props.onMouseLeave?.(e); },
      })}

      {open && targetNode && (
        <Popper
          open={open}
          targetRef={targetNode}
          arrowRef={arrowRef}
          placement={placement as Placement}
          modifiers={modifiers}
        >
          <Wrap show={open} $backgroundColor={backgroundColor} data-testid="tooltip">
            <Block style={blockStyles}>{title}</Block>
            
            {arrow && (
              <Corner ref={setArrowRef}>
                <Arrow />
                <ArrowVert />
              </Corner>
            )}
          </Wrap>
        </Popper>
      )}
    </>
  );
});

export default Tooltip;